import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { Toaster } from 'sonner';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { LoadingProvider } from "./contexts/LoadingProvider";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { AdminRoute } from "./components/auth/AdminRoute";
import { ScrollToTop } from "./components/common/ScrollToTop";
import PageLoader from "./components/common/PageLoader";
import { AppErrorBoundary } from "./components/common/AppErrorBoundary";
import AppLayout from "./layout/AppLayout";
import { isMarketplaceEnabled, isOpportunityFeedEnabled } from "./config/features";

// Auth pages - kept eager since they're entry points
import AcceptInvitation from "./pages/AuthPages/AcceptInvitation";
import SignIn from "./pages/AuthPages/SignIn";
import SignUp from "./pages/AuthPages/SignUp";
import PartnerNetworkContact from "./pages/PartnerNetwork/PartnerNetworkContact";
import PartnerNetworkPricing from "./pages/PartnerNetwork/PartnerNetworkPricing";
import PartnerNetworkHowItWorks from "./pages/PartnerNetwork/PartnerNetworkHowItWorks";
import PartnerNetworkAbout from "./pages/PartnerNetwork/PartnerNetworkAbout";
import PartnerNetworkTrust from "./pages/PartnerNetwork/PartnerNetworkTrust";
import PartnerNetworkPrivacy from "./pages/PartnerNetwork/PartnerNetworkPrivacy";
import SendMyBusiness from "./pages/PartnerNetwork/SendMyBusiness";

// Lazy-loaded route components
const DaMatches = lazy(() => import("./pages/Dashboard/DaMatches"));
const DashboardHome = lazy(() => import("./pages/Dashboard/DashboardHome"));
const SavedDaMatches = lazy(() => import("./pages/Dashboard/SavedDaMatches"));
// The pre-pivot outreach dashboard, still reachable from Old Tabs while the marketplace
// transition runs. It no longer owns "/" — that is the DA matches view now.
const Home = lazy(() => import("./pages/Dashboard/Home"));
const AlertSettings = lazy(() => import("./pages/Dashboard/AlertSettings"));
const MarketplaceDashboard = lazy(() => import("./pages/Marketplace/MarketplaceDashboard"));
const Search = lazy(() => import("./pages/Search"));
const OpportunityFeed = lazy(() => import("./pages/Opportunities/OpportunityFeed"));
const OpportunityBoard = lazy(() => import("./pages/Opportunities/OpportunityBoard"));
const DealModalRoute = lazy(() => import("./pages/Opportunities/DealModalRoute"));
const MatchingRules = lazy(() => import("./pages/Opportunities/MatchingRules"));
const SmartCardDesigner = lazy(() => import("./pages/Opportunities/SmartCard/SmartCardDesigner"));
const DevelopmentApplicationDetail = lazy(() => import("./pages/AddressDetails/DevelopmentApplicationDetail"));
const Analytics = lazy(() => import("./pages/Analytics"));
const DevelopmentMap = lazy(() => import("./pages/DevelopmentMap/DevelopmentMap"));
const Reports = lazy(() => import("./pages/Reports/Reports"));
const Notifications = lazy(() => import("./pages/Notifications"));
const FileUploader = lazy(() => import("./pages/FileUploader"));
const FileDownloader = lazy(() => import("./pages/FileDownloader"));
const PdfUploader = lazy(() => import("./pages/PdfUploader"));
const DataExtractionPage = lazy(() => import("./pages/DataExtractionPage"));
const GooglePlacesPage = lazy(() => import("./pages/GooglePlaces/BusinessDetails"));
const UserProfiles = lazy(() => import("./pages/UserProfiles"));
const BusinessSettings = lazy(() => import("./pages/BusinessSettings"));
const BusinessDetails = lazy(() => import("./pages/BusinessDetails/BusinessDetails"));
const Billing = lazy(() => import("./pages/Billing/Billing"));
const Plan = lazy(() => import("./pages/Plan/Plan"));
const AddBusiness = lazy(() => import("./pages/AddBusiness/AddBusiness"));
const AdminDashboard = lazy(() => import("./components/Admin/AdminDashboard"));
const EmailPreview = lazy(() => import("./components/Admin/EmailPreview"));
const Subscriptions = lazy(() => import("./components/Admin/Subscriptions"));
const SubscriptionDetails = lazy(() => import("./components/Admin/SubscriptionDetails"));
const ContactRequests = lazy(() => import("./components/Admin/ContactRequests"));
const ExclusiveDispatches = lazy(() => import("./components/Admin/ExclusiveDispatches"));
const AdminDeals = lazy(() => import("./pages/Admin/Deals/AdminDeals"));
const InfoPacks = lazy(() => import("./pages/Admin/InfoPacks/InfoPacks"));
const AdminDealModalRoute = lazy(() => import("./pages/Admin/Deals/AdminDealModalRoute"));
const Security = lazy(() => import("./components/Admin/Security"));
const NotFound = lazy(() => import("./pages/OtherPage/NotFound"));
const EmailConfirmation = lazy(() => import("./components/auth/EmailConfirmation"));
const ResendConfirmation = lazy(() => import("./components/auth/ResendConfirmation"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const TermsPage = lazy(() => import("./pages/OtherPage/Terms"));
const Unsubscribe = lazy(() => import("./pages/OtherPage/Unsubscribe"));
const PartnerNetwork = lazy(() => import("./pages/PartnerNetwork/PartnerNetwork"));
const HomePage = lazy(() => import("./pages/PartnerNetwork/HomePage"));
const PartnerNetworkResults = lazy(() => import("./pages/PartnerNetwork/PartnerNetworkResults"));
const PartnerNetworkBusiness = lazy(() => import("./pages/PartnerNetwork/PartnerNetworkBusiness"));
const SubscriptionPage = lazy(() => import("./pages/Subscription/subscription"));
const SubscribeDemo = lazy(() => import("./pages/Subscription/SubscribeDemo"));
const StripeProvider = lazy(() => import("./components/payment/StripeProvider"));
const SandboxStripeProvider = lazy(() => import("./components/payment/SandboxStripeProvider"));
const Integrations = lazy(() => import("./pages/Integrations/Integrations"));
const MarketingList = lazy(() => import("./pages/Marketing/MarketingList"));
const MarketingBuilder = lazy(() => import("./pages/Marketing/MarketingBuilder"));
const Enquiries = lazy(() => import("./pages/Enquiries/Enquiries"));

/**
 * /marketplace/business/:id -> /business/:id, keeping the id.
 *
 * A bare <Navigate> cannot do this: the target depends on the matched param, so the redirect has to
 * read it. Without this, every profile link already shared — and they are the ones people actually
 * share — would land on the marketplace root instead of the business.
 */
function MarketplaceBusinessRedirect() {
    const { id } = useParams();
    return <Navigate to={`/business/${id ?? ""}`} replace />;
}

/**
 * The opportunity-feed routes, as an element rather than JSX inlined in the route table.
 *
 * Exported because the nesting below is a load-bearing claim and nothing rendered can prove it:
 * flattening the deal route to a sibling of the board still renders a deal at the same URL, it
 * just unmounts the board while doing it. As an element, the subtree can be fed to
 * `createRoutesFromElements` and matched — a test then sees the deal resolve through the board —
 * which is a statement about the route tree rather than about how this file happens to be typed.
 *
 * A deal opens at its OWN url, as a CHILD of the board route, so the board stays mounted and
 * renders it through its <Outlet />. That is what makes the link shareable, the back button a
 * close, and the pipeline still visible behind the modal. Its own Suspense boundary, rendering
 * nothing: the app-wide one would swap the whole page for a loader while this chunk arrived,
 * taking the board with it.
 */
export const opportunityRoutes = (
    <>
        <Route path="/opportunities" element={<OpportunityFeed />} />
        <Route path="/opportunities/board" element={<OpportunityBoard />}>
            <Route path="deal/:id" element={<Suspense fallback={null}><DealModalRoute /></Suspense>} />
        </Route>
        <Route path="/opportunities/matching-rules" element={<MatchingRules />} />
        <Route path="/opportunities/smart-card" element={<SmartCardDesigner />} />
    </>
);

/**
 * DA Connect's own prospect pipeline, and the prospect opened over it.
 *
 * Exported for the same reason `opportunityRoutes` above is, and it is the same claim: the nesting
 * is load-bearing and nothing rendered can prove it. Flattened to a sibling of the pipeline route,
 * a prospect still resolves at the same URL — it just unmounts the board or grid while doing it,
 * and the `<Outlet />` on `AdminDeals` then has nothing to draw. As an element, the subtree can be
 * fed to `createRoutesFromElements` and matched, which is a statement about the route TREE rather
 * than about how this file happens to be typed.
 *
 * Its own Suspense boundary, rendering nothing: the page-wide one would swap the whole pipeline for
 * a loader while this chunk arrived, taking the board with it.
 */
export const adminDealsRoutes = (
    <Route path="/admin/deals" element={<Suspense fallback={<PageLoader />}><AdminDeals /></Suspense>}>
        <Route path="deal/:id" element={<Suspense fallback={null}><AdminDealModalRoute /></Suspense>} />
    </Route>
);

/**
 * The app-wide error boundary around the routes, reset by the path so the back button or a link
 * leaves a crashed page without a full reload. Inside the Router because it reads the location.
 */
function RoutesErrorBoundary({ children }: { children: ReactNode }) {
    const { pathname } = useLocation();
    return <AppErrorBoundary resetKey={pathname}>{children}</AppErrorBoundary>;
}

export default function App() {
    useEffect(() => {
        // Hide the initial loading spinner once the app has loaded
        const loadingElement = document.querySelector('.app-loading');
        if (loadingElement) {
            // Add fade out animation
            loadingElement.classList.add('fade-out');
            // Hide the element after animation completes
            setTimeout(() => {
                loadingElement.classList.add('hidden');
            }, 300);
        }
    }, []);

    return (
        <LoadingProvider>
            <AuthProvider>
                <Router>
                    <ScrollToTop />
                    <Toaster richColors position="top-right" />
                    <RoutesErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                        <Routes>
                            {/* Protected Routes */}
                            <Route element={<ProtectedRoute />}>
                                {/* Dashboard Layout */}
                                <Route element={<AppLayout />}>
                                    {/* The app's home. It used to be "/", which is the public
                                        marketplace now — see the block further down. */}
                                    <Route path="/dashboard" element={<DashboardHome />} />

                                    {/* WITH THE MARKETPLACE GATED OFF — production, today — the root
                                        stays exactly what it was. Without this, moving the
                                        marketplace to "/" would leave production's root falling
                                        through to NotFound, because every marketplace route below is
                                        behind isMarketplaceEnabled(). Turning that gate on hands "/"
                                        to the public page and leaves the app on /dashboard. */}
                                    {!isMarketplaceEnabled() && <Route index path="/" element={<DaMatches />} />}
                                    <Route path="/saved" element={<SavedDaMatches />} />
                                    <Route path="/old-dashboard" element={<Home />} />
                                    <Route path="/alert-settings" element={<AlertSettings />} />
                                    {/* The owner's editor for their marketplace listing. Inside the app chrome,
                                        unlike the public page it edits, which is bare and routed below. */}
                                    {/* One page: the listing's performance AND its items. The
                                        separate editor route is gone — it meant two pages each
                                        loading the same listing and a round trip to change a
                                        price. */}
                                    <Route path="/my-listing" element={<MarketplaceDashboard />} />
                                    {/* The owner's inbox for messages sent from the public marketplace profile.
                                        Same gate as the rest of the marketplace surface — with it off there is
                                        no listing to send an enquiry from, and no Enquiries nav entry either. */}
                                    {isMarketplaceEnabled() && (
                                        <Route path="/enquiries" element={<Enquiries />} />
                                    )}
                                    <Route path="/search" element={<Search />} />
                                    {/* Marketplace is NOT here — it's a fully-bare public page routed below,
                                        outside AppLayout, so it renders without the sidebar/header. */}
                                    {/* Opportunity feed — prototype for partner demos, staging + local dev only.
                                        The server refuses these endpoints in production regardless. */}
                                    {/* Declared as `opportunityRoutes` above this component, deal
                                        route nested inside the board route — see that comment. */}
                                    {isOpportunityFeedEnabled() && opportunityRoutes}
                                    {/* One page for every council. "south-australia" is itself the councilKey the
                                        server uses for SA, so this single dynamic route already serves old SA links
                                        unchanged — no separate SA route or redirect needed. */}
                                    <Route path="/development-application/:councilKey/:id" element={<DevelopmentApplicationDetail />} />
                                    <Route path="/analytics" element={<Analytics />} />
                                    <Route path="/development-map" element={<DevelopmentMap />} />
                                    <Route path="/reports" element={<Reports />} />
                                    <Route path="/notifications" element={<Notifications />} />

                                    {/* File Management */}
                                    <Route path="file-uploader" element={<FileUploader />} />
                                    <Route path="file-downloader" element={<FileDownloader />} />
                                    <Route path="pdf-uploader" element={<PdfUploader />} />
                                    <Route path="data-extraction" element={<DataExtractionPage />} />
                                    <Route path="google-places" element={<GooglePlacesPage />} />

                                    {/* Others Page */}
                                    <Route path="/profile" element={<UserProfiles />} />
                                    <Route path="/business-settings" element={<BusinessSettings />} />
                                    {/* The customer's own business record — details, matching preferences and
                                        which councils they receive. Separate from /business-settings above,
                                        which is the pre-pivot page built around the exclusive business card. */}
                                    <Route path="/business-details" element={<BusinessDetails />} />
                                    <Route path="/integrations" element={<Suspense fallback={<PageLoader />}><Integrations /></Suspense>} />
                                    <Route path="/marketing" element={<Suspense fallback={<PageLoader />}><MarketingList /></Suspense>} />
                                    <Route path="/marketing/:id" element={<Suspense fallback={<PageLoader />}><MarketingBuilder /></Suspense>} />
                                    <Route path="/billing" element={<Suspense fallback={<PageLoader />}><Billing /></Suspense>} />
                                    {/* Display-only pricing for the flat-monthly model the business
                                        is moving to. It reads and writes NOTHING from Stripe — both
                                        of its plan buttons route to /billing above, which is the
                                        real billing surface. See Plan.tsx's own file comment. */}
                                    <Route path="/plan" element={<Suspense fallback={<PageLoader />}><Plan /></Suspense>} />
                                    <Route path="/add-business" element={<AddBusiness />} />

                                    {/* Management Routes - Admin + SuperAdmin (page-level auth) */}
                                    <Route path="/admin/subscriptions/:id" element={<SubscriptionDetails />} />
                                    <Route path="/admin/subscriptions" element={<Subscriptions />} />
                                    <Route path="/admin/contact-requests" element={<ContactRequests />} />
                                    <Route path="/admin/exclusive-dispatches" element={<ExclusiveDispatches />} />
                                    <Route path="/admin/info-packs" element={<Suspense fallback={<PageLoader />}><InfoPacks /></Suspense>} />
                                    {/* Declared as `adminDealsRoutes` above this component, the
                                        prospect route nested inside it — see that comment. */}
                                    {adminDealsRoutes}
                                    <Route path="/admin/security" element={<Security />} />

                                    {/* Admin Routes - Protected by AdminRoute (SuperAdmin only) */}
                                    <Route element={<AdminRoute />}>
                                        <Route path="/admin/background-services" element={<AdminDashboard />} />
                                        <Route path="/admin/email-preview" element={<EmailPreview />} />
                                    </Route>

                                    {/* Other protected routes can go here */}
                                </Route>
                            </Route>

                            {/* Auth Layout */}
                            <Route path="/login" element={<SignIn />} />
                            {/* Was the sign-in path for the life of the product — kept so saved
                                passwords, bookmarks and any link already sent still land. */}
                            <Route path="/signin" element={<Navigate to="/login" replace />} />
                            <Route path="/signup" element={<SignUp />} />
                            {/* /freetrial is where the marketing site sends people; it renders the
                                same plain signup as /signup — no trial is promised any more. */}
                            <Route path="/freetrial" element={<SignUp />} />
                            {/* Where an emailed team invitation lands. Public route: the person
                                accepting may have no login yet and is sent to sign in or register
                                first, coming back with the token intact. */}
                            <Route path="/accept-invitation" element={<AcceptInvitation />} />
                            <Route path="/terms" element={<TermsPage />} />
                            <Route path="/confirm-email" element={<EmailConfirmation />} />
                            <Route path="/resend-confirmation" element={<ResendConfirmation />} />
                            <Route path="/reset-password" element={<ResetPasswordPage />} />
                            <Route path="/reset-password/confirm" element={<ResetPasswordPage />} />

                            {/* THE PUBLIC SITE. The marketplace is the front door now, so it owns
                                "/" and its pages sit at the root — /pricing, /about, /contact — which
                                is what a visitor would guess and what reads properly when shared.

                                Public and fully bare (no app chrome), so this lives outside
                                ProtectedRoute/AppLayout even though the sidebar links into it. Still
                                gated to staging + local dev; with the gate off these paths do not
                                exist and "/" stays the app's home above. */}
                            {isMarketplaceEnabled() && (
                                <>
                                    {/* The DA-intelligence landing page, and the marketplace one click away. */}
                                    <Route path="/" element={<HomePage />} />
                                    <Route path="/find" element={<PartnerNetwork />} />
                                    <Route path="/results" element={<PartnerNetworkResults />} />
                                    <Route path="/business/:id" element={<PartnerNetworkBusiness />} />
                                    {/* Public contact form. Posts to the existing /api/public/contact
                                        pipeline, which is the same inbox an operator reads at
                                        /admin/contact-requests. */}
                                    <Route path="/contact" element={<PartnerNetworkContact />} />
                                    <Route path="/pricing" element={<PartnerNetworkPricing />} />
                                    <Route path="/how-it-works" element={<PartnerNetworkHowItWorks />} />
                                    <Route path="/about" element={<PartnerNetworkAbout />} />
                                    <Route path="/trust" element={<PartnerNetworkTrust />} />
                                    <Route path="/privacy" element={<PartnerNetworkPrivacy />} />
                                    {/* Send my Business: printed Planning & Development Assistance Packs. */}
                                    <Route path="/send-my-business" element={<SendMyBusiness />} />

                                    {/* WHERE THESE USED TO LIVE. Kept so anything already shared —
                                        a link in an email, a bookmark, a search result — still
                                        arrives rather than hitting NotFound. */}
                                    <Route path="/marketplace" element={<Navigate to="/find" replace />} />
                                    <Route path="/marketplace/results" element={<Navigate to="/results" replace />} />
                                    <Route path="/marketplace/business/:id" element={<MarketplaceBusinessRedirect />} />
                                    <Route path="/marketplace/contact" element={<Navigate to="/contact" replace />} />
                                    <Route path="/marketplace/pricing" element={<Navigate to="/pricing" replace />} />
                                    <Route path="/marketplace/how-it-works" element={<Navigate to="/how-it-works" replace />} />
                                    <Route path="/marketplace/about" element={<Navigate to="/about" replace />} />
                                    <Route path="/marketplace/trust" element={<Navigate to="/trust" replace />} />
                                    <Route path="/marketplace/privacy" element={<Navigate to="/privacy" replace />} />
                                    {/* NOT to a marketplace page: /terms already renders the real
                                        terms document (public/content/terms.md). */}
                                    <Route path="/marketplace/terms" element={<Navigate to="/terms" replace />} />
                                    <Route path="/partner-network" element={<Navigate to="/find" replace />} />
                                    <Route path="/partner-network/results" element={<Navigate to="/results" replace />} />
                                </>
                            )}

                            {/* Subscription - Accessible without authentication */}
                            <Route path="/subscription" element={<Suspense fallback={<PageLoader />}><StripeProvider><SubscriptionPage /></StripeProvider></Suspense>} />

                            {/* Sandbox Subscription - For shareholder demos with test Stripe */}
                            <Route path="/sandbox/subscription" element={<Suspense fallback={<PageLoader />}><SandboxStripeProvider><SubscriptionPage sandboxMode={true} /></SandboxStripeProvider></Suspense>} />

                            {/* Subscribe / free-trial MOCKUP - shown inside the real dashboard chrome
                                (sidebar + header) but UNAUTHENTICATED so the flow can be previewed. */}
                            <Route element={<AppLayout />}>
                                <Route path="/subscribe-demo" element={<Suspense fallback={<PageLoader />}><SubscribeDemo /></Suspense>} />
                            </Route>

                            {/* Unsubscribe - Accessible without authentication */}
                            <Route path="/unsubscribe" element={<Unsubscribe />} />

                            {/* Fallback Route */}
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </Suspense>
                    </RoutesErrorBoundary>
                </Router>
            </AuthProvider>
        </LoadingProvider>
    );
}
